import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Camera, AlertTriangle, Loader2, FileImage } from "lucide-react";

const CONF = {
  high: "bg-primary/10 text-primary",
  medium: "bg-[hsl(43,74%,52%)]/15 text-[hsl(38,70%,30%)]",
  low: "bg-destructive/10 text-destructive",
};
const FIELD_LABEL = {
  dm_pct: "Dry matter", cp_pct: "Crude protein", me: "ME", ndf_pct: "NDF", adf_pct: "ADF",
  lignin_pct: "Lignin", starch_pct: "Starch", sugar_pct: "Sugar/WSC", ash_pct: "Ash", oil_pct: "Oil/Fat",
  rdp_pct_cp: "RDP", rup_pct_cp: "RUP", soluble_protein_pct_cp: "Soluble protein",
  ca_pct: "Calcium", p_pct: "Phosphorus", mg_pct: "Magnesium", k_pct: "Potassium",
  na_pct: "Sodium", s_pct: "Sulphur", cl_pct: "Chloride",
  cu_mg: "Copper", zn_mg: "Zinc", mn_mg: "Manganese", se_mg: "Selenium",
};

export function ScanLabelDialog({ farmId, feeds, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [values, setValues] = useState({});
  const [meta, setMeta] = useState({ name: "", manufacturer: "", batch_number: "", analysis_date: "" });
  const [dmConfirmed, setDmConfirmed] = useState(false);
  const [mode, setMode] = useState("create");
  const [targetId, setTargetId] = useState("");

  const reset = () => { setFile(null); setPreview(null); setValues({}); setMeta({ name: "", manufacturer: "", batch_number: "", analysis_date: "" }); setDmConfirmed(false); setMode("create"); setTargetId(""); };

  const scan = async () => {
    if (!file) return toast.error("Choose or capture a photo first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/farms/${farmId}/feeds/scan-label`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      const v = {};
      data.fields.forEach((f) => { v[f.feed_field] = f.normalized_value; });
      setValues(v);
      setMeta({ name: data.product_name || "", manufacturer: data.manufacturer || "", batch_number: data.batch_number || "", analysis_date: data.analysis_date || "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not read the image"); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    if (!meta.name.trim()) return toast.error("Confirm the feed name");
    if (!dmConfirmed) return toast.error("Confirm the dry-matter basis");
    if (mode !== "create" && !targetId) return toast.error("Select the feed to update");
    setBusy(true);
    try {
      const feed = await api.post(`/farms/${farmId}/feeds/scan-label/${preview.import_id}/commit`, {
        mode, feed_id: mode === "create" ? null : targetId, name: meta.name,
        dm_basis_confirmed: dmConfirmed, values,
        manufacturer: meta.manufacturer, batch_number: meta.batch_number, analysis_date: meta.analysis_date,
      });
      toast.success(mode === "create" ? "Feed created from label" : "Feed analysis updated");
      setOpen(false); reset(); onSaved?.(feed.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <Button variant="outline" className="rounded-full" data-testid="scan-label-button" onClick={() => setOpen(true)}>
        <Camera className="h-4 w-4 mr-1" /> Scan feed label
      </Button>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Scan feed label</DialogTitle>
          <DialogDescription>Take a photo or upload a manufacturer label / lab tag (JPG, PNG, HEIC). Values are read by GPT‑4o and shown for your review before saving — nothing is saved automatically.</DialogDescription>
        </DialogHeader>

        {!preview ? (
          <div className="space-y-4 py-2">
            <label className="flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-border p-8 cursor-pointer hover:border-primary transition-colors" data-testid="scan-dropzone">
              <FileImage className="h-8 w-8 text-primary" />
              <span className="text-sm text-muted-foreground">{file ? file.name : "Tap to capture or choose an image"}</span>
              <input type="file" accept="image/*,.heic,.heif" capture="environment" className="hidden" data-testid="scan-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            <Button className="w-full rounded-full" onClick={scan} disabled={busy || !file} data-testid="scan-upload-btn">
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reading label…</> : "Read label"}
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="scan-review">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">Source: {preview.source_type === "laboratory_analysis" ? "Laboratory analysis" : "Feed label"}</span>
              <span className="text-xs text-muted-foreground">{preview.filename} · {preview.model} · {preview.parser_version}</span>
              {preview.duplicate && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[hsl(43,74%,52%)]/15 text-[hsl(38,70%,30%)]" data-testid="scan-duplicate">Duplicate upload</span>}
            </div>

            {(preview.warnings || []).map((w, i) => (
              <div key={i} className="flex items-start gap-2 rounded-lg bg-destructive/5 border border-destructive/30 p-2.5 text-sm text-destructive" data-testid="scan-warning">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />{w}
              </div>
            ))}

            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Feed name *</Label><Input value={meta.name} data-testid="scan-name-input" onChange={(e) => setMeta({ ...meta, name: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Manufacturer</Label><Input value={meta.manufacturer} onChange={(e) => setMeta({ ...meta, manufacturer: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Batch / lot</Label><Input value={meta.batch_number} onChange={(e) => setMeta({ ...meta, batch_number: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Analysis date</Label><Input value={meta.analysis_date} onChange={(e) => setMeta({ ...meta, analysis_date: e.target.value })} className="mt-1" /></div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-border">
              <Table>
                <TableHeader><TableRow><TableHead>Nutrient</TableHead><TableHead>Source text</TableHead><TableHead>Original</TableHead><TableHead>Confidence</TableHead><TableHead className="text-right">Value (normalized)</TableHead></TableRow></TableHeader>
                <TableBody>
                  {preview.fields.map((f) => (
                    <TableRow key={f.feed_field} data-testid={`scan-field-${f.feed_field}`} className={f.needs_review ? "bg-destructive/[0.03]" : ""}>
                      <TableCell className="font-medium text-[hsl(148,38%,16%)]">{FIELD_LABEL[f.feed_field] || f.feed_field}{f.needs_review && <span className="ml-1 text-destructive" title={f.note}>*</span>}</TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[160px] truncate" title={f.source_text}>{f.source_text}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{f.value} {f.unit || "?"}{f.basis && f.basis !== "unknown" ? ` · ${f.basis}` : ""}</TableCell>
                      <TableCell><span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${CONF[f.confidence]}`}>{f.confidence}</span></TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Input type="number" step="0.01" value={values[f.feed_field] ?? ""} data-testid={`scan-value-${f.feed_field}`} onChange={(e) => setValues({ ...values, [f.feed_field]: e.target.value === "" ? "" : Number(e.target.value) })} className="w-24 text-right" />
                          <span className="text-xs text-muted-foreground w-14">{f.normalized_unit}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {preview.fields.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No nutrient values recognised. You can still create the feed and enter values manually.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
            {preview.fields.some((f) => f.needs_review) && <p className="text-xs text-destructive">* Flagged for review — check the value, unit or basis before saving. As-fed values are not auto-converted to a DM basis.</p>}

            <label className="flex items-center gap-2 text-sm" data-testid="scan-dm-confirm-row">
              <Checkbox checked={dmConfirmed} data-testid="scan-dm-confirm" onCheckedChange={setDmConfirmed} />
              I confirm the feed name and the dry-matter basis of these values.
            </label>

            <div className="rounded-lg border border-border p-3">
              <Label className="text-xs">Save as</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {[["create", "New feed"], ["update", "Update existing feed"], ["new_analysis", "New analysis of existing feed"]].map(([m, lbl]) => (
                  <button key={m} data-testid={`scan-mode-${m}`} onClick={() => setMode(m)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${mode === m ? "bg-primary text-white border-primary" : "border-border text-muted-foreground"}`}>{lbl}</button>
                ))}
              </div>
              {mode !== "create" && (
                <div className="mt-3">
                  <Label className="text-xs">Target feed</Label>
                  <Select value={targetId} onValueChange={setTargetId}>
                    <SelectTrigger data-testid="scan-target-select" className="mt-1"><SelectValue placeholder="Select feed to update" /></SelectTrigger>
                    <SelectContent>{feeds.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground mt-1">The current values are preserved in this feed's analysis history — nothing is overwritten silently.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {preview && (
          <DialogFooter>
            <Button variant="ghost" onClick={reset} data-testid="scan-back-btn">Scan another</Button>
            <Button onClick={commit} disabled={busy} className="rounded-full" data-testid="scan-commit-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save reviewed values"}</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
